import { type Board } from '@air/api/types';

const LOCATION_CRUMB_SEPARATOR = ' / ';

/** A root board with no library is its own location, so a lone crumb says nothing. */
const BOARD_LOCATION_MINIMUM_CRUMBS = 2;

/** Beyond this the middle collapses: the card is 240px wide, so a deep trail would wrap to a paragraph. */
const BOARD_LOCATION_MAXIMUM_CRUMBS = 3;

const LOCATION_CRUMB_ELLIPSIS = '…';

/** How many crumbs survive at the end of a collapsed trail: the board and the parent it sits in. */
const BOARD_LOCATION_TRAILING_CRUMBS = BOARD_LOCATION_MAXIMUM_CRUMBS - 1;

/** Library / ancestors / leaf, blanks skipped; `null` when nothing is left to say. */
export const joinWorkspaceAgentLocationCrumbs = (crumbs: (string | null | undefined)[]): string | null => {
  const present = crumbs.filter((crumb): crumb is string => !!crumb);

  return present.length > 0 ? present.join(LOCATION_CRUMB_SEPARATOR) : null;
};

export type WorkspaceAgentBoardLocation = {
  /** Every crumb, for the location row's tooltip. */
  fullTrail: string;
  /** What the row draws: middle-collapsed to `library / … / parent / board` past three crumbs. */
  label: string;
};

/** Same trail as the asset-modal location breadcrumbs: library, ancestors outermost first, board. */
export const getWorkspaceAgentBoardLocation = (board: Board): WorkspaceAgentBoardLocation | null => {
  const crumbs = [
    board.library?.title,
    ...(board.ancestors ?? []).map((ancestor) => ancestor.title),
    board.title,
  ].filter((crumb): crumb is string => !!crumb);

  if (crumbs.length < BOARD_LOCATION_MINIMUM_CRUMBS) {
    return null;
  }

  const fullTrail = crumbs.join(LOCATION_CRUMB_SEPARATOR);

  if (crumbs.length <= BOARD_LOCATION_MAXIMUM_CRUMBS) {
    return { fullTrail, label: fullTrail };
  }

  return {
    fullTrail,
    label: [crumbs[0], LOCATION_CRUMB_ELLIPSIS, ...crumbs.slice(-BOARD_LOCATION_TRAILING_CRUMBS)].join(
      LOCATION_CRUMB_SEPARATOR,
    ),
  };
};
